import SimpleDropdown from '~/shared/components/SimpleDropdown';
import Skeleton from 'react-loading-skeleton';
import {
  CUSTOM_VOLUME_VIEW_LABEL,
  VOLUME_VIEW_PRESETS,
  VolumeViewPresetId,
  getVolumeViewLabel,
  resolveVolumeView,
} from '~/shared/utils/volumeViewPresets';
import Typography from '../Typography';

interface VolumeViewDropdownProps {
  appliedAutoscaleState: string[];
  appliedEligibility: string[];
  onSelectPreset: (presetId: VolumeViewPresetId) => void;
  presetCounts?: Partial<Record<VolumeViewPresetId, number>>;
  isLoading?: boolean;
  selectedCount?: number;
}

interface VolumeViewDropdownContentProps extends VolumeViewDropdownProps {
  isLoading: boolean;
}

function VolumeViewDropdownContent({
  appliedAutoscaleState,
  appliedEligibility,
  onSelectPreset,
  presetCounts = {},
  isLoading,
  selectedCount,
}: VolumeViewDropdownContentProps) {
  const resolution = resolveVolumeView(appliedAutoscaleState, appliedEligibility);
  const selectedLabel = getVolumeViewLabel(resolution);
  const selectedPreset = VOLUME_VIEW_PRESETS.find((preset) => preset.label === selectedLabel);
  const selectedPresetCount =
    selectedPreset && presetCounts[selectedPreset.id] !== undefined
      ? presetCounts[selectedPreset.id]
      : undefined;
  const selectedCountToDisplay = selectedCount ?? selectedPresetCount;
  const items =
    resolution === 'custom'
      ? [CUSTOM_VOLUME_VIEW_LABEL, ...VOLUME_VIEW_PRESETS.map((preset) => preset.label)]
      : VOLUME_VIEW_PRESETS.map((preset) => preset.label);

  if (isLoading) {
    return (
      <div className="min-w-[290px]">
        <Skeleton
          containerClassName="block leading-[unset!important]"
          className="w-full h-[36px] 4xl:h-[44px] leading-[unset!important] rounded-[8px]"
          baseColor="var(--color-gray-200)"
          highlightColor="#FFF"
        />
      </div>
    );
  }

  return (
    <SimpleDropdown
      aria-label="Volume view"
      className="min-w-[290px]"
      menuButtonClassName=" h-[36px]! 4xl:h-[44px]!"
      items={items}
      selectedItem={selectedLabel}
      displayLabel={selectedLabel}
      menuButtonLabelClassName="text-primary-900 font-semibold"
      renderItemEnd={(label) => {
        const preset = VOLUME_VIEW_PRESETS.find((item) => item.label === label);
        const countFromPreset = preset ? presetCounts[preset.id] : undefined;
        const isSelectedItem = label === selectedLabel;
        const count = isSelectedItem ? selectedCountToDisplay : countFromPreset;
        return (
          <Typography
            variant="buttons-2"
            color="text-gray-600"
          >
            {count === undefined ? '' : count.toLocaleString()}
          </Typography>
        );
      }}
      onSelect={(label) => {
        const preset = VOLUME_VIEW_PRESETS.find((item) => item.label === label);
        if (preset) {
          onSelectPreset(preset.id);
        }
      }}
      renderButtonEnd={() => (
        <Typography variant="buttons-2" color="text-gray-600">
          {selectedCountToDisplay === undefined ? '' : selectedCountToDisplay.toLocaleString()}
        </Typography>
      )}
    />
  );
}

export default function VolumeViewDropdown(props: VolumeViewDropdownProps) {
  return (
    <VolumeViewDropdownContent
      appliedAutoscaleState={props.appliedAutoscaleState}
      appliedEligibility={props.appliedEligibility}
      onSelectPreset={props.onSelectPreset}
      presetCounts={props.presetCounts}
      isLoading={props.isLoading ?? false}
      selectedCount={props.selectedCount}
    />
  );
}
